import React from 'react';
import { motion } from 'framer-motion';
import { 
  FiMapPin, 
  FiPhone, 
  FiMail, 
  FiClock,
  FiMessageCircle,
  FiFacebook,
  FiInstagram
} from 'react-icons/fi';
import { FaWhatsapp, FaXTwitter } from 'react-icons/fa6';
import { useContent } from '../context/ContentContext';
import './ContactPage.css';

const ContactPage = () => {
  const { content } = useContent();

  // WhatsApp linki oluştur
  const whatsappPhone = content.contact?.whatsappPhone || '905551234567';
  const whatsappMessage = encodeURIComponent(content.contact?.whatsappMessage || 'Merhaba, bilgi almak istiyorum.');
  const whatsappLink = `https://wa.me/${whatsappPhone}?text=${whatsappMessage}`;

  const contactInfo = [
    {
      icon: FiMapPin,
      title: 'Adres',
      value: content.contact?.address || 'Atatürk Caddesi No: 123, Merkez/İstanbul'
    },
    {
      icon: FiPhone,
      title: 'Telefon',
      value: content.contact?.phone || '0212 123 45 67',
      link: `tel:${(content.contact?.phone || '02121234567').replace(/\s/g, '')}`
    },
    {
      icon: FaWhatsapp,
      title: 'WhatsApp',
      value: content.contact?.whatsappPhone ? `0${content.contact.whatsappPhone.slice(2)}` : '0555 123 45 67',
      link: whatsappLink,
      isWhatsapp: true
    },
    {
      icon: FiMail,
      title: 'E-posta',
      value: content.contact?.email || 'info@bmkegitim.com',
      link: `mailto:${content.contact?.email || 'info@bmkegitim.com'}`
    },
    {
      icon: FiClock,
      title: 'Çalışma Saatleri',
      value: content.contact?.workingHours || 'Pazartesi - Cumartesi: 09:00 - 21:00'
    }
  ];

  const socialLinks = [
    { icon: FiFacebook, url: content.contact?.socialMedia?.facebook || '#', label: 'Facebook' },
    { icon: FiInstagram, url: content.contact?.socialMedia?.instagram || '#', label: 'Instagram' },
    { icon: FaXTwitter, url: content.contact?.socialMedia?.twitter || '#', label: 'X' }
  ];

  return (
    <div className="contact-page">
      {/* Hero */}
      <section className="contact-hero">
        <div className="contact-hero-bg"></div>
        <div className="container">
          <motion.div 
            className="contact-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>İletişim</h1>
            <p>Sorularınız için bize ulaşın, size yardımcı olmaktan mutluluk duyarız</p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Info */}
            <motion.div 
              className="contact-info"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2>Bize Ulaşın</h2>
              <p className="contact-info-desc">
                Eğitim programlarımız, kayıt işlemleri veya diğer konularda sorularınız için bize ulaşabilirsiniz.
              </p>

              <div className="info-cards">
                {contactInfo.map((info, index) => (
                  <motion.div 
                    key={index}
                    className="info-card"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="info-icon">
                      <info.icon />
                    </div>
                    <div className="info-content">
                      <h4>{info.title}</h4>
                      {info.link ? (
                        <a href={info.link}>{info.value}</a>
                      ) : (
                        <p>{info.value}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="social-section">
                <h4>Sosyal Medya</h4>
                <div className="social-links">
                  {socialLinks.map((social, index) => (
                    <a 
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.label}
                    >
                      <social.icon />
                    </a>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* WhatsApp Section */}
            <motion.div 
              className="whatsapp-container"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="whatsapp-card">
                <div className="whatsapp-icon">
                  <FaWhatsapp />
                </div>
                <h2>WhatsApp'tan Yazın</h2>
                <p>
                  Sorularınız için bize WhatsApp üzerinden anında ulaşabilirsiniz. 
                  Hızlı ve kolay iletişim için aşağıdaki butona tıklayın.
                </p>
                <div className="whatsapp-features">
                  <div className="feature-item">
                    <FiMessageCircle />
                    <span>Anında Yanıt</span>
                  </div>
                  <div className="feature-item">
                    <FiClock />
                    <span>7/24 Ulaşılabilir</span>
                  </div>
                </div>
                <a 
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whatsapp-btn"
                >
                  <FaWhatsapp />
                  WhatsApp'tan Mesaj Gönder
                </a>
                <p className="whatsapp-note">
                  Tıkladığınızda WhatsApp uygulaması açılacaktır
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section">
        <div className="map-container">
          <iframe 
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3184.3405779382338!2d35.27277057718852!3d37.04936635361239!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x152889054af0883f%3A0x7a132d6edf04ac11!2sBmk%20E%C4%9Fitim%20Kurttepe%20%C5%9Eube!5e0!3m2!1str!2str!4v1764203183852!5m2!1str!2str"
            width="100%" 
            height="100%" 
            style={{ border: 0 }} 
            allowFullScreen="" 
            loading="lazy" 
            referrerPolicy="no-referrer-when-downgrade"
            title="BMK Eğitim Konum"
          />
        </div>
      </section>
    </div>
  );
};

export default ContactPage;

